// src/components/NavBar.js
import React, { Fragment, useEffect, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, BellAlertIcon, BellIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.png';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AvatarLocation from './AvatarLocation';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserData } from '../context/UserData';
import LoadingSpinner from './LoadingSpinner';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const NavBar = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);

  const userData = useUserData();

  const [button1, setButton1] = useState(false);
  const [button2, setButton2] = useState(false);
  const [button3, setButton3] = useState(false);
  const [button4, setButton4] = useState(false);

  useEffect(() => {
    if (userData?.users) {
      fetchCount();
      fetchButtonConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const fetchCount = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/alert_crud.php?getPmCount=${userData?.users?.pm}`);
      setCount(response.data.records?.record_count || 0);
      setAlertCount(response.data.alert_count || 0);
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchButtonConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/config_system_crud.php`, { params: { getButtons: true } });
      if (response.data[0] === 'success') {
        const button = response.data[1] || {};
        setButton1(button.button_1_link || false);
        setButton2(button.button_2_link || false);
        setButton3(button.button_3_link || false);
        setButton4(button.button_4_link || false);
      } else {
        setButton1(false); setButton2(false); setButton3(false); setButton4(false);
      }
    } catch (error) {
      console.error('Error fetching button configuration:', error);
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else {
        toast.error('An error occurred.', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const response = await axios.post(`${apiUrl}/users_login.php`, { status: 'signout', data: {} }, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data.success) {
        navigate('/login');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast('An error occurred while trying to sign out.', { position: 'top-center', autoClose: 5000 });
    }
  };

  return (
    <Disclosure as="nav" className="sticky top-0 z-50 bg-gray-800">
      {({ open, close }) => (
        <>
          <div className="mx-auto max-w-[1280px] px-4 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              {/* Left: Logo + Desktop links */}
              <div className="flex items-center gap-3">
                <img className="h-8 w-auto" src={logo} alt="Aileron-Systems-Mainframe" />
                <div className="hidden sm:block">
                  <div className="flex space-x-2">
                    <NavLink
                      to="/"
                      className={({ isActive }) =>
                        isActive
                          ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                          : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                    >
                      Dashboard
                    </NavLink>

                    <NavLink
                      to="/service_dashboard"
                      className={({ isActive }) =>
                        isActive
                          ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                          : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                    >
                      Service
                    </NavLink>

                    <NavLink
                      to="/customers"
                      className={({ isActive }) =>
                        isActive
                          ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                          : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                    >
                      Customers
                    </NavLink>

                    <NavLink
                      to="/wip_list/aj"
                      className={({ isActive }) =>
                        isActive
                          ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                          : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                    >
                      WIP
                    </NavLink>

                    {button1 && (
                      <NavLink
                        to={`${button1.path}`}
                        className={({ isActive }) =>
                          isActive
                            ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                            : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      >
                        {button1.label}
                      </NavLink>
                    )}

                    {button2 && (
                      <NavLink
                        to={`${button2.path}`}
                        className={({ isActive }) =>
                          isActive
                            ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                            : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      >
                        {button2.label}
                      </NavLink>
                    )}

                    {button3 && (
                      <NavLink
                        to={`${button3.path}`}
                        className={({ isActive }) =>
                          isActive
                            ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                            : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      >
                        {button3.label}
                      </NavLink>
                    )}

                    {button4 && (
                      <NavLink
                        to={`${button4.path}`}
                        className={({ isActive }) =>
                          isActive
                            ? 'rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white'
                            : 'rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      >
                        {button4.label}
                      </NavLink>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Desktop user actions */}
              <div className="hidden sm:block">
                <div className="flex items-center gap-3">
                  {/* PM count */}
                  <NavLink to="/invoice_update">
                    <button
                      type="button"
                      className="relative rounded-full bg-gray-800 p-1 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                    >
                      <span className="sr-only">PM Count</span>
                      <span className="inline-flex items-center rounded-full bg-red-50 px-1.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                        {loading ? <LoadingSpinner /> : userData?.users?.pm ? count : ''}
                      </span>
                    </button>
                  </NavLink>

                  {/* Alerts */}
                  <NavLink to="/alerts_view">
                    <button
                      type="button"
                      className="relative rounded-full bg-gray-800 p-1 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                    >
                      <span className="sr-only">Alerts</span>
                      {alertCount >= 1 ? (
                        <>
                          <BellAlertIcon className="animate-ping absolute inline-flex h-6 w-6 text-red-600 opacity-75" />
                          <BellAlertIcon className="relative inline-flex h-6 w-6 text-red-600" />
                        </>
                      ) : (
                        <BellIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </NavLink>

                  {/* Profile menu */}
                  <Menu as="div" className="relative">
                    <Menu.Button className="relative flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                      <span className="sr-only">Open user menu</span>
                      <AvatarLocation className="h-8 w-8 rounded-full" avatar={userData?.avatar || 'no_pic.png'} />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to={`/profile/${userData?.users?.id}`}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              Your Profile
                            </NavLink>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to="/settings/holidays"
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              Admin Settings
                            </NavLink>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to="system_config/button_config"
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              System Config
                            </NavLink>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleSignOut}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block w-full text-left px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="flex sm:hidden">
                <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? <XMarkIcon className="block h-6 w-6" aria-hidden="true" /> : <Bars3Icon className="block h-6 w-6" aria-hidden="true" />}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile full-screen sheet */}
          <Disclosure.Panel className="sm:hidden">
            {({ close }) => (
              <>
                {/* overlay that closes on tap */}
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" onClick={() => close()} />

                <div className="fixed inset-x-0 top-0 mt-16 h-[calc(100vh-64px)] overflow-y-auto rounded-t-2xl bg-white shadow-2xl ring-1 ring-gray-200">
                  {/* user header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <AvatarLocation className="h-10 w-10 rounded-full" avatar={userData?.avatar || 'no_pic.png'} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {userData?.users?.first_name} {userData?.users?.last_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {userData?.users?.title} {userData?.users?.department ? `| ${userData.users.department}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ml-auto rounded-full p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <span className="sr-only">Alerts</span>
                      {alertCount >= 1 ? (
                        <>
                          <BellAlertIcon className="animate-ping absolute inline-flex h-5 w-5 text-red-600 opacity-75" />
                          <BellAlertIcon className="relative inline-flex h-5 w-5 text-red-600" />
                        </>
                      ) : (
                        <BellIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </div>

                  {/* nav items */}
                  <div className="px-3 py-3 space-y-2">
                    {[
                      { to: '/', label: 'Dashboard' },
                      { to: '/service_dashboard', label: 'Service' },
                      { to: '/customers', label: 'Customers' },
                      { to: '/wip_list/aj', label: 'WIP' },
                      { to: '/full_viewer_mobile', label: 'Full Schedule' },
                      { to: '/office_calendar', label: 'Office Vacation Calendar' },
                    ].map(({ to, label }) => (
                      <NavLink
                        key={label}
                        to={to}
                        onClick={() => close()}
                        className={({ isActive }) =>
                          classNames(
                            'flex items-center justify-between rounded-xl px-3 py-3 text-sm font-medium shadow-sm ring-1 transition-colors',
                            isActive
                              ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                              : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
                          )
                        }
                      >
                        <span className="truncate">{label}</span>
                        <ChevronRightIcon className="h-5 w-5 text-gray-300" />
                      </NavLink>
                    ))}
                  </div>

                  {/* footer actions */}
                  <div className="px-4 py-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2">
                      <NavLink
                        to={`/profile/${userData?.users?.id}`}
                        onClick={() => close()}
                        className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 text-center"
                      >
                        Profile
                      </NavLink>

                      {userData?.users?.department === 'Admin' ? (
                        <NavLink
                          to="/t/"
                          onClick={() => close()}
                          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 text-center"
                        >
                          Tech Mode
                        </NavLink>
                      ) : (
                        <span />
                      )}

                      <NavLink
                        to="/vacation_req"
                        onClick={() => close()}
                        className="col-span-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 text-center"
                      >
                        Vacation Request
                      </NavLink>

                      <button
                        onClick={() => {
                          close();
                          handleSignOut();
                        }}
                        className="col-span-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 text-center"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>

                  {/* safe area */}
                  <div className="h-4" />
                </div>
              </>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default NavBar;
